
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from "@ng-bootstrap/ng-bootstrap";
import { FeatureKeys, FeatureService } from "company/components/features/feature.service";
import { first, union, unionWith } from "lodash";
import { SearchOptionsFactory } from "rl-common/factories/index";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { IEntitySearchDoc } from "rl-common/models/i-entity-search-doc";
import { IQueryNode } from "rl-common/models/i-query-node";
import { IRecordTitle } from "rl-common/models/i-record-title";
import { RelationshipTypes } from "rl-common/models/relationship-types";
import { CharTypeId, RelationshipIndicatorIds } from "rl-common/rl-common.consts";
import { IEntityRelationshipState } from "rl-common/services/entity/entity-relationship.models";
import { ParentEntityService } from "rl-common/services/entity/parent-entity/parent-entity.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { SearchService } from "rl-common/services/search/search.service";
import { QueryUtil } from "rl-common/utils/query.util";
import { combineLatest, Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";
import { ClipboardSelectionGridComponent } from "../../../clipboard/clipboard-selection-grid/clipboard-selection-grid.component";
import { EntitySearchComponent } from "../../../entities/entity-search/entity-search.component";
import { SearchOptions, SelectType } from "../../../entities/entity-search/entity-search.models";
import { SearchFieldNames } from "../../../entities/entity-search/query.models";
import { EntityRelationshipDataSelectStrategy } from "../../../grid/datasource/data-select/entity-relationship-data-select.strategy";
import { AssocModuleSelectDataSource } from "../../../grid/datasource/search/assoc-module-select.datasource";
import { SqlAssocEntitySearchDataSource } from "../../../grid/datasource/search/sql-assoc-entity-search.datasource";
import { IndexedRowData } from "../../../grid/models/indexed-row-data";
import { AssociationChipsComponent } from "../../../new-association-modal/new-association-wizard/select-associations/association-chips/association-chips.component";
import { InputListComponent, IValidateEvent } from "./input-list/input-list.component";


@Component({
	selector: "rl-sql-component-relationship",
	templateUrl: "./sql-component-relationship.component.html",
	styleUrls: ["./sql-component-relationship.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, EntitySearchComponent, InputListComponent, NgbNavOutlet, AssociationChipsComponent, ClipboardSelectionGridComponent]
})
export class SqlComponentRelationshipComponent implements OnInit, OnDestroy, OnChanges {
	private readonly _parentEntityService = inject(ParentEntityService);
	private readonly _oneConfigService = inject(OneConfigService);
	private readonly _searchService = inject(SearchService);
	private readonly _featureService = inject(FeatureService);

	@Input()
	assocCharTypeId: number;

	@Input()
	charTypeId: number;

	@Input()
	dataSource: SqlAssocEntitySearchDataSource<IEntitySearchDoc, unknown>;

	@Output()
	onEntityRelationshipChange = new EventEmitter<IEntityRelationshipState<number, IEntitySearchDoc>>();

	isTablesOrRights = false;
	selectedDataFromSearch: IRecordTitle[] = [];
	private readonly _subs: Subscription[] = [];
	gridCount = 0;
	inputListCount = 0;
	selectedInputList: IEntitySearchDoc[] = [];
	searchOptions: SearchOptions = {} as SearchOptions;
	parentCharTypeId: CharTypeId;
	parentRecordId: number;
	direction: RelationshipTypes = RelationshipTypes.Child;
	matchesParentQuery: IQueryNode;
	shouldUpdateSelectedRows = false;

	get isAssociatingChild() {
		return this.direction === RelationshipTypes.Child;
	}
	get parentTemplateId() {
		return this._parentEntityService.templateId;
	}

	ngOnInit(): void {
		this.buildSearchOptions();
		this.isTablesOrRights = this.assocCharTypeId === CharTypeId.Right || this.assocCharTypeId === CharTypeId.Table || (this.assocCharTypeId === CharTypeId.Catalog && (this.charTypeId === CharTypeId.Right || this.charTypeId === CharTypeId.Table));
		const sub = combineLatest([this.dataSource.dataSelectStrategy.selectStateChange$, this.dataSelectStrategy.netChanges$])
			.pipe(filter(rd => !!rd))
			.subscribe(([_selectedState, _nc]) => {
				this.emit();
			});

		const sub2 = this.dataSource.indexedRowData$
			.pipe(
				filter((ird) => !!ird),
				take(1)
			)
			.subscribe((initialRowData) => {
				if (!this.dataSelectStrategy?.defaultSelectedIds) {
					return;
				}

				if (this.dataSelectStrategy.selectAllEnabled && this.dataSource.rowCount$.value > 1
					&& this.dataSelectStrategy.defaultSelectedIds.length === this.dataSource.rowCount$.value) {
					if (this.searchOptions.parentCharTypeId === CharTypeId.Catalog && this.searchOptions.charTypeId === CharTypeId.Catalog) {
						let defSelectedIdsLength = this.dataSelectStrategy.defaultSelectedIds.length;
						if (this.dataSelectStrategy.defaultSelectedIds.find(d => d === this.searchOptions.parentRecordId)) {
							defSelectedIdsLength--;
							if (defSelectedIdsLength === this.dataSource.rowCount$.value) {
								this.dataSelectStrategy.selectAll();
							} else {
								const selectedRowData = initialRowData.filter(rd => this.dataSelectStrategy.defaultSelectedIds.includes(rd.data.recordID));
								if (selectedRowData.length > 0) {
									this.dataSource.dataSelectStrategy.selectRows(selectedRowData);
								}
							}
						}
					} else {
						this.dataSelectStrategy.selectAll();
					}
				} else if (this.dataSelectStrategy.isRequired && initialRowData.length === 1 && this.assocCharTypeId === CharTypeId.Catalog && !this.dataSelectStrategy.isInactiveCatalog(first(initialRowData).data)
				) {
					this.dataSelectStrategy.selectRow(first(initialRowData));
				} else {
					this.shouldUpdateSelectedRows = true;
					this.updateSelectedRowsOnPageChange(initialRowData);
				}
			});
		const sub5 = this.dataSource.indexedRowData$
			.pipe(
				filter((ird) => !!ird),
			)
			.subscribe((rowData) => {
				if (this.shouldUpdateSelectedRows) {
					this.updateSelectedRowsOnPageChange(rowData);
				}
			});

		const sub3 = this.dataSelectStrategy.count$
			.subscribe(c => this.gridCount = c);

		const sub4 = this.dataSource.formGroup.statusChanges.subscribe(() => {
			this.emit();
		});
		this._subs.push(sub, sub2, sub3, sub4, sub5);
	}

	private updateSelectedRowsOnPageChange(initialRowData: IndexedRowData<IEntitySearchDoc>[]) {
		const selectedRecordIds = this.dataSelectStrategy.defaultSelectedIds ?? [];
		const missingRecordIds = selectedRecordIds.filter(
			id => !initialRowData.some(rd => rd.data.recordID === id)
		);

		if (missingRecordIds.length > 0) {
			const query = QueryUtil.$eq_any(
				SearchFieldNames.Entity.recordID,
				missingRecordIds
			);

			this._searchService.search(
				this.assocCharTypeId,
				"",
				query,
				{ start: 0, rows: missingRecordIds.length }
			).subscribe(response => {
				const missingRows = response.documents.map((doc, index) => ({
					index,
					rowPath: null,
					data: doc
				} as IndexedRowData<IEntitySearchDoc>));

				this.applySelectedRows(initialRowData.concat(missingRows));
			});

			return;
		}

		this.applySelectedRows(initialRowData);
	}

	private applySelectedRows(initialRowData: IndexedRowData<IEntitySearchDoc>[]) {
		const netChanges = this.dataSelectStrategy.netChanges$.value;
		let selectedRowData = [];
		if (this.dataSelectStrategy.isAllSelected) {
			selectedRowData = initialRowData.filter(rd =>
				!Array.from(netChanges.deletedIds).includes(rd.data.recordID)
			);
		} else {
			const includedRecordIds = [
				...this.dataSelectStrategy.defaultSelectedIds,
				...netChanges.addedIds
			];
			const excludedRecordIds = Array.from(netChanges.deletedIds);

			selectedRowData = initialRowData.filter(rd =>
				includedRecordIds.includes(rd.data.recordID) &&
				!excludedRecordIds.includes(rd.data.recordID)
			);
		}
		if (selectedRowData.length > 0) {
			this.dataSource.dataSelectStrategy.selectRows(selectedRowData);
		}
	}

	ngOnChanges(changes: ComponentChanges<this>) {
		if (changes.dataSource && changes.dataSource.currentValue && changes.dataSource.currentValue instanceof AssocModuleSelectDataSource) {
			const assocDataSource = changes.dataSource.currentValue as AssocModuleSelectDataSource<IEntitySearchDoc, IEntitySearchDoc>;
			if (assocDataSource.direction !== undefined && assocDataSource.direction !== null) {
				this.direction = assocDataSource.direction;
			}
		}

		// RL-5320/RL-7625/RL-7626: When dataSource changes, apply the current feature flag value
		if (changes.dataSource && changes.dataSource.currentValue && this.assocCharTypeId === CharTypeId.Catalog) {
			// Get current feature flag value and apply it to the new datasource
			this._featureService.isEnabled$(FeatureKeys.InactiveCatalog).pipe(take(1)).subscribe(isEnabled => {
				const dataSource = this.dataSource as unknown as { setInactiveCatalogFeatureEnabled?: (isEnabled: boolean) => void };
				if (typeof dataSource.setInactiveCatalogFeatureEnabled === "function") {
					dataSource.setInactiveCatalogFeatureEnabled(isEnabled);
				} else {
					this.dataSelectStrategy.setInactiveCatalogFeatureEnabled(isEnabled);
				}
			});
		}
	}

	get dataSelectStrategy() {
		return this.dataSource.dataSelectStrategy as EntityRelationshipDataSelectStrategy<IEntitySearchDoc, number, IEntitySearchDoc>;
	}

	private buildSearchOptions() {
		this.searchOptions = this.getSearchOptions();
		let parentTemplateId = null;
		if (this.assocCharTypeId === CharTypeId.Mock) {
			this.searchOptions.charTypeId = this.assocCharTypeId;
		} else if (this._parentEntityService.hasGrandParent()) {
			this.searchOptions.parentCharTypeId = this._parentEntityService.parent.charTypeId;
			this.searchOptions.parentRecordId = this._parentEntityService.parent.recordId;
			parentTemplateId = this._parentEntityService.parent.templateId;
			this.searchOptions.relatedCharTypeId = this._parentEntityService.charTypeId;
			this.searchOptions.relatedRecordId = this._parentEntityService.recordId;
		} else {
			this.searchOptions.parentCharTypeId = this._parentEntityService.charTypeId;
			this.searchOptions.parentRecordId = this._parentEntityService.recordId;
		}
		const validTemplates = this._oneConfigService.getChildAssocTemplateIds(this.searchOptions.parentCharTypeId, parentTemplateId, this.assocCharTypeId);
		this.searchOptions.filteredTemplateIds = validTemplates;
		if (this.searchOptions.parentCharTypeId === CharTypeId.Relationship && this.searchOptions.charTypeId === CharTypeId.Amount) {
			this.searchOptions.selectType = SelectType.Radio;
		}
		this.parentCharTypeId = this.searchOptions.parentCharTypeId;
		this.parentRecordId = this.searchOptions.parentRecordId;
	}

	private getSearchOptions() {
		const options = SearchOptionsFactory.buildTaggedRelationshipOptions(this._parentEntityService.charTypeId, this.assocCharTypeId, this.searchOptions.relatedCharTypeId);
		options.showHierarchy = SearchOptionsFactory.resolveShowHierarchy(this.assocCharTypeId, this._parentEntityService.parent?.charTypeId);
		return options;
	}

	worksheetSelected(docs: IEntitySearchDoc[]) {
		this.updateValidated({
			validRecords: docs,
			validRecordsDeprecated: []
		})
	}

	public updateValidated(event: IValidateEvent) {
		let validRecords = event.validRecords;

		// RL-7759/RL-3988: Filter out inactive Catalogs (universal validation, not feature-flag dependent)
		if (this.assocCharTypeId === CharTypeId.Catalog) {
			validRecords = validRecords.filter(rec => {
				const isInactive = (rec as unknown as { activeIndicator?: number })?.activeIndicator === 0;
				return !isInactive;
			});
		}

		const inputListRecordIds = validRecords.map(r => r.recordID);
		const indexedMatchingRows = this.dataSource.indexedRowData$?.value?.filter(rd => inputListRecordIds.includes(rd.data.recordID)) ?? [];
		const recordIds = indexedMatchingRows.map(x => x.data.recordID);
		const nonIndexedRows = validRecords.filter(x => recordIds.indexOf(x.recordID) < 0);
		const removedRows = event.deselectedRecords ?? [];
		removedRows.forEach(x => {
			this.dataSelectStrategy.deselectRowById(x.recordID, x);
		});
		this.dataSelectStrategy.selectRows(indexedMatchingRows);
		const dataSourceSelected = Array.from(this.dataSource.dataSelectStrategy.selectedState.selectedIds ?? new Set<unknown>());
		this.selectedInputList = validRecords.filter(x => dataSourceSelected.indexOf(x.recordID) === -1);
		if (nonIndexedRows.length > 0) {
			const selectedRows: IndexedRowData<IEntitySearchDoc>[] = [];
			nonIndexedRows.forEach(row => {
				const selectedRow: IndexedRowData<IEntitySearchDoc> = {
					index: row.recordID,
					rowPath: null,
					data: row
				};
				selectedRows.push(selectedRow);
			});
			this.dataSelectStrategy.selectRows(selectedRows);
		}
	}

	public removeSelection(recordTitle: IRecordTitle) {
		// We can't pull the entity search doc from the indexed row data due to page changes or filtering
		// TODO: We need a better way to go from a selected entity to the original grid row data type
		this.dataSource.dataSelectStrategy.deselectRowById(recordTitle.recordId, recordTitle.title);
	}

	private emit() {
		const rowData = this.dataSource.indexedRowData$?.value ?? [];
		const selectedInputOffPage = this.selectedInputList.filter(r => rowData.map(rd => rd.data.recordID).includes(r.recordID));
		this.inputListCount = this.selectedInputList.length;
		const selectedCount = this.getSelectedCount(selectedInputOffPage);
		const selectedIds = union(Array.from(this.dataSelectStrategy.selectedState.selectedIds), this.selectedInputList.map(r => r.recordID));
		const deselectedIds = Array.from(this.dataSelectStrategy.selectedState.deselectedIds);
		const inputListSelected = this.selectedInputList;
		const selectedValues = unionWith([...Array.from(this.dataSelectStrategy.selectedState.selectedValues), ...this.selectedInputList], (s, s2) => s.recordID !== s2.recordID);
		const model = this.dataSource.getSelectedSearchModel()[0];

		const matchesParentQueries = [
			QueryUtil.$eq(SearchFieldNames.Relationship.childCharTypeID, this.assocCharTypeId),
			QueryUtil.$eq(SearchFieldNames.Relationship.parentCharTypeID, this.parentCharTypeId),
			QueryUtil.$eq(SearchFieldNames.Relationship.parentRecordID, this.parentRecordId),
		];
		if (this.assocCharTypeId !== CharTypeId.User && !this.dataSource.isRelatedChild) {
			matchesParentQueries.push(QueryUtil.$eq(SearchFieldNames.Relationship.relInd, RelationshipIndicatorIds.Default));
		}
		const queries = matchesParentQueries;
		if (deselectedIds.length > 0) {
			const deselectedQuery = QueryUtil.$eq_none(SearchFieldNames.Relationship.childRecordID, deselectedIds);
			queries.push(deselectedQuery);
		}
		this.matchesParentQuery = QueryUtil.$join_parent_rel(
			QueryUtil.$and(...queries)
		);
		model.query = this.matchesParentQuery;
		model.rows = 1000;

		// RL-5320/RL-7625/RL-7626: Exclude inactive Catalogs from select-all payload when feature flag is enabled
		if (this.assocCharTypeId === CharTypeId.Catalog && this.dataSelectStrategy.selectedState.isAllSelected) {
			const strategy = this.dataSelectStrategy;
			if (strategy?.isInactiveCatalogFeatureEnabled) {
				model.filterQueries.push(QueryUtil.$neq(SearchFieldNames.Entity.activeIndicator, "0"));
			}
		}

		const isValid = !this.dataSource.formGroup.hasError("requiredGrid");
		this.onEntityRelationshipChange.emit({
			netChanges: this.dataSelectStrategy.netChanges$.value,
			gridSelected: this.dataSelectStrategy.selectedState,
			gridCount: this.dataSource.rowCount$.value,
			inputListSelected,
			defaultSelectedIds: this.dataSelectStrategy.defaultSelectedIds,
			model,
			selectedValues,
			selectedIds,
			isValid,
			selectedCount,
			labelFn: (val) => val.title
		});
	}

	private getSelectedCount(selectedInputOffPage: IEntitySearchDoc[]) {
		if (!this.dataSelectStrategy.selectedState.isAllSelected) {
			return this.dataSelectStrategy.selectedState.selectedIds.size + selectedInputOffPage.length;
		} else {
			return this.dataSource.rowCount$.value - this.dataSelectStrategy.selectedState.deselectedIds.size;
		}
	}

	ngOnDestroy() {
		this._subs.forEach(sub => sub.unsubscribe());
	}
}
